import { ComponentProps, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import ProductsAvailable from '@alltrails/amplitude/enums/ProductsAvailable';
import ProductType from '@alltrails/amplitude/enums/ProductType';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import logUpsellScrollStarted from '@alltrails/amplitude/events/logUpsellScrollStarted';
import logUpsellShown from '@alltrails/amplitude/events/logUpsellShown';
import logWebProModalDismissed from '@alltrails/amplitude/events/logWebProModalDismissed';
import logWebProModalPurchaseButtonTapped from '@alltrails/amplitude/events/logWebProModalPurchaseButtonTapped';
import useUser from '@alltrails/context/hooks/useUser';
import { ModalStep } from '@alltrails/modal';
import HeadToTheAppDialog from '../HeadToTheAppDialog';
import UnableToUpgradeDialog from '../UnableToUpgradeDialog';
import styles from './BaseUpsellStep.module.scss';

type Props = {
  children?: React.ReactNode;
  contentsClassName?: string;
  headerImage?: ComponentProps<typeof ModalStep>['headerImage'];
  heading: React.ReactNode;
  navigateTo: (modalKey: 'checkout' | 'upsell') => void;
  onCloseRequest: () => void;
  onSuccess?: () => void;
  primaryButtonText?: React.ReactNode;
  promoEligibleHeading: React.ReactNode;
  upsellTrigger: UpsellTrigger;
  productType: ProductType;
  productsAvailable: ProductsAvailable;
  skipCheckout?: boolean;
  isCrossPlatform?: boolean;
  onCrossPlatformRequest?: () => void;
};

export default function BaseUpsellStep({
  children,
  contentsClassName,
  headerImage,
  navigateTo,
  onCloseRequest,
  onSuccess,
  primaryButtonText,
  upsellTrigger,
  productType,
  productsAvailable,
  skipCheckout = false,
  isCrossPlatform,
  onCrossPlatformRequest
}: Props) {
  const [headToTheAppDialogState, setHeadToTheAppDialogState] = useState<{ isOpen: false } | { isOpen: true; provider: 'apple' | 'google' }>({
    isOpen: false
  });
  const [isUnableToUpgradeDialogOpen, setIsUnableToUpgradeDialogOpen] = useState(false);
  const hasScrolled = useRef(false);
  const user = useUser();
  const isPromoEligible = user?.isPromoEligible;

  useEffect(() => {
    logUpsellShown({
      purchase_screen_type_plan: isPromoEligible ? 'free trial' : 'non trial',
      trigger: upsellTrigger,
      products_available: productsAvailable
    });
  }, [isPromoEligible, upsellTrigger, productsAvailable]);

  const handleCloseRequest = () => {
    logWebProModalDismissed({
      dismiss_action: 'x out',
      trigger: upsellTrigger,
      purchase_screen_type_plan: isPromoEligible ? 'free trial' : 'non trial',
      products_available: productsAvailable
    });
    onCloseRequest();
  };

  const handleCtaClick = () => {
    logWebProModalPurchaseButtonTapped({
      product_type: productType,
      purchase_screen_type_plan: isPromoEligible ? 'free trial' : 'non trial',
      trigger: upsellTrigger
    });

    // If cross-platform handling is provided, use that instead of default behavior
    if (isCrossPlatform && onCrossPlatformRequest) {
      onCrossPlatformRequest();
      return;
    }

    // If skipCheckout is true, trigger onSuccess immediately
    if (skipCheckout) {
      onSuccess?.();
      return;
    }

    if (user?.canPurchaseNewSubscription || (user?.canUpgradeExistingSubscription && user?.subscription?.provider === 'recurly')) {
      navigateTo('checkout');
    } else if (user?.canUpgradeExistingSubscription && (user?.subscription?.provider === 'apple' || user?.subscription?.provider === 'google')) {
      setHeadToTheAppDialogState({ isOpen: true, provider: user?.subscription?.provider });
    } else {
      setIsUnableToUpgradeDialogOpen(true);
    }
  };

  const primaryButton = {
    onClick: handleCtaClick,
    text:
      primaryButtonText ??
      (isPromoEligible ? <FormattedMessage defaultMessage="Start your free trial" /> : <FormattedMessage defaultMessage="Upgrade" />),
    testId: 'checkout-cta'
  };

  return (
    <ModalStep
      buttons={{
        experimental: true,
        fill: true, // overridden when experimental is true
        primaryButton
      }}
      caption={<FormattedMessage defaultMessage="Cancel anytime" />}
      headerImage={headerImage}
      headerImageV2={Boolean(headerImage)}
      onCloseRequest={handleCloseRequest}
      onScroll={() => {
        if (!hasScrolled.current) {
          logUpsellScrollStarted({
            purchase_screen_type_plan: isPromoEligible ? 'free trial' : 'non trial',
            trigger: upsellTrigger
          });

          hasScrolled.current = true;
        }
      }}
      contentsClassName={contentsClassName}
    >
      {headToTheAppDialogState.isOpen && (
        <HeadToTheAppDialog onCloseRequest={() => setHeadToTheAppDialogState({ isOpen: false })} provider={headToTheAppDialogState.provider} />
      )}
      {isUnableToUpgradeDialogOpen && <UnableToUpgradeDialog onCloseRequest={() => setIsUnableToUpgradeDialogOpen(false)} />}
      <div className={styles.contents}>{children}</div>
    </ModalStep>
  );
}
